import React from "react";
import { Download, X } from "lucide-react";
import { useInstallPrompt } from "../hooks/useInstallPrompt";

/**
 * InstallPrompt displays a banner encouraging users to install the
 * PWA to their home screen. It only appears on browsers that support
 * the `beforeinstallprompt` event (Chrome, Edge, Samsung Internet,
 * etc.) and only when the app is not already installed.
 *
 * The banner is dismissible and will not reappear until the next
 * page load.
 */
const InstallPrompt: React.FC = () => {
  const { isPromptAvailable, isInstalled, promptInstall, dismissPrompt } =
    useInstallPrompt();

  if (!isPromptAvailable || isInstalled) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-lg bg-emerald-600 px-4 py-3 text-white shadow-lg print:hidden sm:left-auto sm:right-4"
    >
      <div className="flex items-start gap-3">
        <Download size={20} className="mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Install SevenServings</p>
          <p className="mt-0.5 text-xs text-emerald-100">
            Add to your home screen for a faster, app-like experience with
            offline access.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={promptInstall}
              className="rounded bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-1 focus:ring-offset-emerald-600"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismissPrompt}
              className="rounded px-3 py-1.5 text-xs font-medium text-emerald-100 transition-colors hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-1 focus:ring-offset-emerald-600"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismissPrompt}
          aria-label="Dismiss install prompt"
          className="shrink-0 rounded p-1 text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
